import { t } from 'i18next';
import { isBrowser } from './browserHelper.ts';
import {
  COOKIE_TO_IKEAAPP,
  getCookieAsObject,
  setCookie,
} from './cookieHelper.ts';
import colors from '../styles/colors.ts';
import type { AppView } from '../types/app.ts';
import type { ValueOf } from '../types/globals.ts';

export type AppViewParsedQueryParams = {
  ikeaTheme: string;
  redirectUrl: string;
  platform: string;
};

export const APP_VIEW_QUERY_PARAMS = {
  IKEA_THEME: 'ikea_theme',
  REDIRECT_URL: 'redirect_url',
  PLATFORM: 'platform',
  GUEST_USER: 'guest_user',
} as const;

export type AppViewQueryParamKeys = ValueOf<typeof APP_VIEW_QUERY_PARAMS>;

export const isAppViewGuestUser = () => {
  if (!isBrowser()) {
    return false;
  }
  return (
    new URLSearchParams(window.location.search).get(
      APP_VIEW_QUERY_PARAMS.GUEST_USER,
    ) === 'true'
  );
};

const DEFAULT_APP_VIEW_PARSED_QUERY_PARAMS: AppViewParsedQueryParams = {
  ikeaTheme: '',
  redirectUrl: '',
  platform: '',
};

const APP_VIEW_DARK_THEME_KEY_VALUE = 'ikea_theme=dark';
const APP_VIEW_DARK_THEME_ATTRIBUTE = 'ikea-theme-dark';
/* app view cleanup: Remove APP_VIEW_OPERATION in all places once the app team starts using /app instead of /ikeaapp 
Also, remove 'ikeaapp' from the operations in the country configs, then run the Github action 'Delete operation on host' */

export const setAppViewTheme = () => {
  if (!isBrowser()) return;
  if (window.location.search.includes(APP_VIEW_DARK_THEME_KEY_VALUE)) {
    document.body.setAttribute(APP_VIEW_DARK_THEME_ATTRIBUTE, '');
    document.body.style.backgroundColor = colors.background.default;
  } else {
    document.body.removeAttribute(APP_VIEW_DARK_THEME_ATTRIBUTE);
  }
};

export const getAppViewQueryParams = (): AppViewParsedQueryParams => {
  if (!isBrowser()) return DEFAULT_APP_VIEW_PARSED_QUERY_PARAMS;
  const queryParams = new URLSearchParams(window.location.search);
  const { IKEA_THEME, REDIRECT_URL, PLATFORM } = APP_VIEW_QUERY_PARAMS;
  const ikeaTheme = queryParams.get(IKEA_THEME) || '';
  const redirectUrl = queryParams.get(REDIRECT_URL) || '';
  const platform = queryParams.get(PLATFORM) || '';
  return { ikeaTheme, redirectUrl, platform };
};

export const getAppViewQueryParamNames = (): AppViewQueryParamKeys[] => {
  return Object.values(APP_VIEW_QUERY_PARAMS);
};

const getOperationAllowedRedirects = (operation: string): string[] => {
  const links: string[] = [];
  const tocDefaultUrl = t(`pages.contact.termsAndConditions.link.default`);
  const tocOperationUrl = t(
    `pages.contact.termsAndConditions.link.${operation}`,
  );

  if (tocDefaultUrl.includes('http'))
    links.push(encodeURIComponent(tocDefaultUrl));
  if (tocOperationUrl.includes('http'))
    links.push(encodeURIComponent(tocOperationUrl));

  return links;
};

export const setCookieForAppView = (operation: string, view: AppView) => {
  if (view === 'app') {
    const existingCookieValue = getCookieAsObject(COOKIE_TO_IKEAAPP);
    setCookie(COOKIE_TO_IKEAAPP, {
      ...existingCookieValue,
      allowedRedirects: getOperationAllowedRedirects(operation),
    });
  }
};

export const appendNewCookieValueForAppView = (
  cookieNewValue: object,
  view: AppView,
) => {
  const value = {
    ...getCookieAsObject(COOKIE_TO_IKEAAPP),
    ...cookieNewValue,
  };
  if (view === 'app') {
    setCookie(COOKIE_TO_IKEAAPP, value);
  }
};
